import type { Modifier, ModificationsMap, SubstitutionModifier } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the customer-facing ingredient names behind a set of modifiers.
 *
 * Modifiers only carry ingredient IDs, so the names are looked up on the
 * product's modifications. Modifiers whose modification is no longer on the
 * product are dropped, since there is no name to report for them.
 */
export function getModifierIngredientNames(
  modifiers: Modifier[] | undefined,
  allModificationsMap: ModificationsMap | undefined,
) {
  if (!modifiers) return [];

  return modifiers
    .map((modifier) => allModificationsMap?.get(modifier.ingredientId)?.ingredient?.name)
    .filter((ingredientName): ingredientName is string => Boolean(ingredientName));
}

/**
 * Resolves substitutions into `removed → added` pairs.
 */
export function getSubstitutionIngredientNames(
  substitutions: SubstitutionModifier[] | undefined,
  allModificationsMap: ModificationsMap | undefined,
) {
  if (!substitutions) {
    return [];
  }

  return substitutions
    .map((substitution) => {
      const removedName = allModificationsMap?.get(substitution.removedIngredientId)?.ingredient?.name; // prettier-ignore
      const addedName = allModificationsMap?.get(substitution.addedIngredientId)?.ingredient?.name; // prettier-ignore

      if (!removedName || !addedName) {
        return;
      }

      return `${removedName} → ${addedName}`;
    })
    .filter((substitutionName): substitutionName is string => Boolean(substitutionName));
}
